import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Skeleton,
  Typography,
} from '@mui/material';
import { SelectProps } from '@mui/material/Select/Select';
import React, {
  Dispatch,
  SetStateAction,
  UIEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import get from 'lodash/get';
import { backgroundColorDarkTheme } from '../../styles/global.styles';
import TextFieldComponent from '../TextFieldComponent';
import { IPaginationParams } from '../../services/types';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import CheckboxComponent from '../CheckboxComponent';

type DropdownSelectComponent = SelectProps & {
  options?: any[];
  optionsCount?: number;
  labelField?: string;
  valueField?: string;
  onSelectedValues?: (value: any[]) => void;
  selectedValues?: any[] | null;
  errorMessage?: string;
  setPagination?: Dispatch<SetStateAction<IPaginationParams>>;
  pagination?: IPaginationParams;
  filter?: string;
  setFilter?: Dispatch<SetStateAction<any>>;
  setMissingIds?: Dispatch<SetStateAction<any>>;
  missingItems?: any[];
};

const DropdownSelectComponent = ({
  options,
  optionsCount,
  labelField,
  valueField,
  errorMessage,
  pagination,
  setPagination,
  filter,
  onSelectedValues,
  selectedValues,
  setFilter,
  setMissingIds,
  missingItems,
  ...props
}: DropdownSelectComponent) => {
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [optionsValues, setOptionsValues] = useState<any[]>([]);
  const debounceSearch = useRef<NodeJS.Timeout>();
  const debounceScroll = useRef<NodeJS.Timeout>();
  const [searchLoading, setSearchLoading] = useState(false);
  const [search, setSearch] = useState<string>('');
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [open, setOpen] = useState(false);

  const renderValue = (value: any[]) => {
    const render: string[] = [];
    if (optionsValues && optionsValues.length > 0) {
      if (value && value.length > 0) {
        value.forEach((val) => {
          const item = optionsValues.find((v) => get(v, valueField) === val);
          if (item) render.push(get(item, labelField));
        });
      }
    }
    return render.join(', ');
  };

  const onValueChange = (event: any) => {
    const values = event.target.value as any[];
    setSelectedItems(values);
  };

  const onSearchChange = (value: string) => {
    if (setFilter && labelField && setPagination) {
      clearTimeout(debounceSearch.current);
      setSearch(value);
      setSearchLoading(true);

      debounceSearch.current = setTimeout(() => {
        setFilter((prev: any) => ({
          ...prev,
          [labelField]: value.trim() !== '' ? value.trim() : '',
        }));

        setPagination((prev) => ({
          ...prev,
          pageNumber: 1,
        }));
      }, 1000);
    }
  };

  const isArraysEquals = (arr1: any[], arr2: any[]) => {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((item) => arr2.includes(item));
  };

  const onScrollChange = (event: UIEvent<HTMLDivElement>) => {
    clearTimeout(debounceScroll.current);

    debounceScroll.current = setTimeout(() => {
      const target = event.target as HTMLDivElement;

      if (target.scrollTop + target.clientHeight >= target.scrollHeight - 6) {
        onMenuScrolling();
      }
    }, 300);
  };

  const onMenuScrolling = () => {
    if (
      !searchLoading &&
      setPagination &&
      optionsValues.length < (optionsCount || 0)
    ) {
      setPagination((prev) => ({
        ...prev,
        pageNumber: prev.pageNumber + 1,
      }));
    }
  };

  const handleSelectAll = (value: boolean) => {
    if (value) {
      setIsSelectAll(true);
      setSelectedItems(optionsValues.map((item) => get(item, valueField)));
    } else {
      setIsSelectAll(false);
      setSelectedItems([]);
    }
  };

  useEffect(() => {
    if (options && options.length > 0 && !pagination && !filter) {
      setOptionsValues([...options]);
    }

    if (
      options &&
      options.length > 0 &&
      pagination?.pageNumber == 1 &&
      filter
    ) {
      setOptionsValues([...options]);
    }

    if (options && options.length > 0 && pagination) {
      setOptionsValues((prev) => [
        ...prev,
        ...options.filter(
          (item) =>
            !prev.some((v) => get(v, valueField) == get(item, valueField)),
        ),
      ]);
    }

    if (!options || (options && options.length <= 0)) {
      setOptionsValues([]);
    }
  }, [options, pagination, filter, valueField]);

  useEffect(() => {
    if (!isArraysEquals(selectedItems, selectedValues || [])) {
      setSelectedItems(selectedValues || []);
    }
  }, [selectedValues]);

  useEffect(() => {
    if (onSelectedValues) {
      onSelectedValues(selectedItems);
    }

    if (selectedItems && selectedItems.length > 0 && setMissingIds) {
      const validSelectedItems = selectedItems.filter(
        (i) => i !== undefined && i !== null,
      );

      const optionValuesSet = new Set(
        optionsValues
          .map((o) => get(o, valueField))
          .filter((v) => v !== undefined),
      );

      const notOnPage = validSelectedItems.filter(
        (i) => !optionValuesSet.has(i),
      );

      if (notOnPage.length > 0) setMissingIds(notOnPage);
    }
  }, [selectedItems]);

  useEffect(() => {
    if (missingItems && missingItems.length > 0) {
      setOptionsValues((prev) => [
        ...prev,
        ...missingItems.filter(
          (m) => !prev.some((p) => get(p, valueField) === get(m, valueField)),
        ),
      ]);
    }
  }, [missingItems, valueField]);

  useEffect(() => {
    setSearchLoading(false);
  }, [optionsValues]);

  return (
    <Box sx={{ marginBottom: '24px' }}>
      <FormControl fullWidth>
        <InputLabel id="drop-select-label" sx={{ color: '#ffffff' }}>
          {props.label}
        </InputLabel>
        <Select
          labelId="drop-select-label"
          id="drop-select"
          fullWidth
          sx={{ border: 'gray' }}
          multiple
          value={selectedValues}
          renderValue={(value: any) => renderValue(value)}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={(event: any) => onValueChange(event)}
          IconComponent={() =>
            open ? (
              <ArrowDropUp sx={{ color: '#ffffff' }} />
            ) : (
              <ArrowDropDown sx={{ color: '#ffffff' }} />
            )
          }
          slotProps={{
            input: { sx: { borderColor: 'gray', color: '#ffffff' } },
          }}
          input={<OutlinedInput label={props.label} />}
          MenuProps={{
            onScrollCapture: (event) => onScrollChange(event),
            style: {
              maxHeight: '350px',
            },
          }}
          {...props}
        >
          <Box
            sx={{
              backgroundColor: backgroundColorDarkTheme,
              p: 3,
              mb: 0,
              pb: 0,
            }}
          >
            {(pagination && setPagination && filter && setFilter) == null &&
              options && (
                <TextFieldComponent
                  placeholder={'Filtre aqui'}
                  onChangeText={(val) =>
                    setOptionsValues(
                      options.filter(
                        (v) =>
                          labelField &&
                          get(v, labelField)
                            .toLowerCase()
                            .indexOf(val.toLowerCase()) > -1,
                      ),
                    )
                  }
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => {
                    event.stopPropagation();
                  }}
                />
              )}
            {(pagination && setPagination && filter && setFilter) != null && (
              <TextFieldComponent
                placeholder={'Filtre aqui'}
                onChangeText={(val) => onSearchChange(val)}
                value={search}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              />
            )}
            <CheckboxComponent
              label={isSelectAll ? 'Limpar Seleção' : 'Selecionar Todos'}
              value={isSelectAll}
              onChange={(val) => handleSelectAll(val)}
            />
            <Divider
              orientation={'horizontal'}
              sx={{ borderColor: 'gray', mb: '12px' }}
            />
          </Box>

          {optionsValues.length > 0 &&
            optionsValues.map((item) => {
              const itemValue = valueField ? get(item, valueField) : item;
              const itemLabel = labelField ? get(item, labelField) : item;
              const isSelected = selectedItems.includes(itemValue);

              return !searchLoading ? (
                <MenuItem
                  key={itemValue}
                  value={itemValue}
                  sx={{ backgroundColor: backgroundColorDarkTheme }}
                >
                  <Checkbox checked={isSelected} />
                  <ListItemText primary={itemLabel} />
                </MenuItem>
              ) : (
                <MenuItem
                  key={itemValue}
                  value={itemValue}
                  sx={{ backgroundColor: backgroundColorDarkTheme }}
                  disabled
                >
                  <Skeleton variant={'text'} width={'100%'} height={40} />
                </MenuItem>
              );
            })}

          {optionsValues.length === 0 && !searchLoading && (
            <MenuItem
              sx={{
                backgroundColor: backgroundColorDarkTheme,
              }}
              disabled
            >
              Oops... Nenhum item encontrado.
            </MenuItem>
          )}

          {optionsValues.length === 0 && searchLoading && (
            <MenuItem
              sx={{ backgroundColor: backgroundColorDarkTheme }}
              disabled
            >
              <Skeleton variant={'text'} width={'100%'} height={40} />
            </MenuItem>
          )}
        </Select>
      </FormControl>
      {errorMessage && (
        <Typography fontSize={12} color={'#ff0000'} sx={{ mt: 0.5, mb: 1 }}>
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};

export default DropdownSelectComponent;
